import { contrastRatio, hslTriplet, type Hsl } from './color';

/**
 * Personal chrome colours, so one operator running several brands can tell
 * their tabs apart.
 *
 * This is NOT `boTheme` (`theme.backoffice.*` on the organization row). That
 * one is the BRAND's colour: it is stored server-side, edited on /site, and
 * every admin of that brand sees it. This one is a per-person, per-brand
 * preference kept in the browser, and it deliberately wins over the brand
 * colour — the whole point is that the person, not the brand, decides which
 * of their five tabs is the green one.
 *
 * Both set the same custom-property names, so they are kept on separate
 * elements rather than fighting over one — see `accentElement()` below.
 */

/**
 * Ten hues spread far enough around the wheel that two tabs are never a
 * second look apart. Lightness and the text colour are derived, not chosen
 * per theme, so a new hue cannot ship an unreadable button.
 */
export const ACCENT_THEMES = [
  { id: 'blue', hue: 220, sat: 83 },
  { id: 'indigo', hue: 250, sat: 72 },
  { id: 'violet', hue: 282, sat: 72 },
  { id: 'plum', hue: 318, sat: 66 },
  { id: 'rose', hue: 348, sat: 77 },
  { id: 'orange', hue: 22, sat: 88 },
  { id: 'amber', hue: 45, sat: 88 },
  { id: 'emerald', hue: 148, sat: 62 },
  { id: 'teal', hue: 172, sat: 66 },
  { id: 'cyan', hue: 195, sat: 85 },
] as const;

export type AccentTheme = (typeof ACCENT_THEMES)[number];

/** No personal colour — the brand's own colour (or the b1fr0st default) stands. */
export const DEFAULT_ACCENT = 'default';

export type AccentId = typeof DEFAULT_ACCENT | AccentTheme['id'];

export type ColorMode = 'light' | 'dark';

export function isAccentId(value: unknown): value is AccentId {
  return (
    value === DEFAULT_ACCENT ||
    ACCENT_THEMES.some((t) => t.id === value)
  );
}

export function accentTheme(id: AccentId): AccentTheme | null {
  return ACCENT_THEMES.find((t) => t.id === id) ?? null;
}

/**
 * Text laid over `bg`, picked by measured contrast rather than by eye.
 *
 * A single rule cannot serve the whole wheel: at the same lightness a blue
 * needs white text and an amber needs near-black. Measuring both candidates
 * and keeping the better one is what lets every theme share one lightness
 * ramp and still clear 4.5:1 — `accent-themes.test.ts` asserts the floor.
 */
function readableOn(bg: Hsl, hue: number): Hsl {
  const onLight: Hsl = [hue, 25, 99];
  const onDark: Hsl = [hue, 55, 8];
  return contrastRatio(bg, onLight) >= contrastRatio(bg, onDark) ? onLight : onDark;
}

/** The three surfaces every floor is expressed over, so neither list can lose one. */
type SurfaceSet<T> = Record<'background' | 'muted' | 'secondary', T>;

/**
 * The page surfaces `--primary` has to stay legible against, mirroring the
 * `:root` / `.dark` blocks of `index.css`. `accent-themes.test.ts` re-reads that
 * stylesheet and fails if these drift out of step with it.
 *
 * `--primary` is not only a button fill: `button.tsx`'s `link` variant and
 * several figures around the app render it AS TEXT on the page. A palette
 * judged only on the text drawn ON primary passes while being unreadable IN
 * primary, which is how the first cut of this file shipped an amber at 2.25:1.
 *
 * `--secondary` is listed even though it currently holds the same colour as
 * `--muted` in both blocks: the accent REWRITES that property, so a surface
 * left out here is a surface the accent can move without anything noticing —
 * which it did, two lightness points down and five hues below AA.
 */
export const SURFACES: Record<ColorMode, SurfaceSet<Hsl>> = {
  light: { background: [0, 0, 100], muted: [210, 40, 96.1], secondary: [210, 40, 96.1] },
  dark: {
    background: [222.2, 84, 4.9],
    muted: [217.2, 32.6, 17.5],
    secondary: [217.2, 32.6, 17.5],
  },
};

/**
 * Floors the primary has to clear on each surface.
 *
 * All are WCAG AA (4.5) with headroom except the dark greys (`--muted` and the
 * `--secondary` that matches it), which are held to 3.97 — what the stock
 * palette manages there — instead of to AA.
 *
 * AA on that surface is affordable rather than impossible, so the exception is
 * a choice: measured, it costs blue 62->66, indigo 70->72, violet 63->67, plum
 * 58->63 and rose 61->65 lightness, and the other five hues already clear it.
 * Those points are the difference between an accent and a pastel of it, and no
 * accent ends up worse than the palette already shipping — which is the whole
 * claim the exception rests on.
 */
const SURFACE_FLOORS: Record<ColorMode, SurfaceSet<number>> = {
  light: { background: 4.75, muted: 4.55, secondary: 4.55 },
  dark: { background: 4.9, muted: 3.97, secondary: 3.97 },
};

/** AA for the label drawn on top of a primary-filled button. */
const ON_PRIMARY_FLOOR = 4.6;

/**
 * The lightness a hue has to sit at, found by measuring rather than fixed.
 *
 * Nominal lightness is not perceived lightness: at L=45 a blue is dark enough
 * to read as text on white and an amber is nowhere near, so one shared value
 * for all ten hues cannot work — that is exactly how five of these themes first
 * shipped below AA. Light mode walks down from the brightest candidate and dark
 * mode walks up from the darkest, so each hue keeps as much punch as it can
 * while still clearing every floor.
 *
 * The loops are bounded; a hue that satisfies nothing returns the last
 * candidate and is caught by the palette's contrast tests rather than silently
 * shipping.
 */
function pickLightness(hue: number, sat: number, mode: ColorMode): number {
  const floors = SURFACE_FLOORS[mode];
  const surfaces = SURFACES[mode];
  const candidates =
    mode === 'light'
      ? Array.from({ length: 33 }, (_, i) => 48 - i)
      : Array.from({ length: 33 }, (_, i) => 56 + i);
  let last = candidates[0]!;
  for (const l of candidates) {
    last = l;
    const primary: Hsl = [hue, sat, l];
    const clearsEverySurface = (Object.keys(surfaces) as (keyof typeof surfaces)[]).every(
      (name) => contrastRatio(primary, surfaces[name]) >= floors[name],
    );
    const labelReads = contrastRatio(primary, readableOn(primary, hue)) >= ON_PRIMARY_FLOOR;
    if (clearsEverySurface && labelReads) return l;
  }
  return last;
}

interface Ramp {
  primary: Hsl;
  primaryForeground: Hsl;
  secondary: Hsl;
  sidebar: Hsl;
  sidebarForeground: Hsl;
  sidebarBorder: Hsl;
}

const RAMPS = new Map<string, Ramp>();

/**
 * The accent's `--secondary`: the stylesheet's own secondary, tinted.
 *
 * The lightness is taken from `SURFACES` rather than chosen, because a chosen
 * one drifts. This shipped at 94 in light mode — darker than the 96.1 the
 * palette is measured on — and put plum at 4.30 and rose at 4.29 against their
 * own primary, under AA and under the 4.72 the stock pair manages. Borrowing
 * the measured lightness leaves the tint as the only difference, worth at most
 * 0.06 of a contrast ratio across the ten hues.
 */
function secondarySurface(hue: number, mode: ColorMode): Hsl {
  const saturation = mode === 'light' ? 30 : 28;
  return [hue, saturation, SURFACES[mode].secondary[2]];
}

function ramp(theme: AccentTheme, mode: ColorMode): Ramp {
  const key = `${theme.id}:${mode}`;
  const cached = RAMPS.get(key);
  if (cached) return cached;

  const { hue, sat } = theme;
  const saturation = mode === 'light' ? sat : Math.min(sat + 6, 95);
  const primary: Hsl = [hue, saturation, pickLightness(hue, saturation, mode)];
  const built: Ramp =
    mode === 'light'
      ? {
          primary,
          primaryForeground: readableOn(primary, hue),
          secondary: secondarySurface(hue, mode),
          sidebar: [hue, 42, 96],
          sidebarForeground: [hue, 38, 20],
          sidebarBorder: [hue, 30, 86],
        }
      : {
          primary,
          primaryForeground: readableOn(primary, hue),
          secondary: secondarySurface(hue, mode),
          sidebar: [hue, 32, 10],
          sidebarForeground: [hue, 22, 92],
          sidebarBorder: [hue, 25, 22],
        };
  RAMPS.set(key, built);
  return built;
}

/**
 * The custom properties an accent owns.
 *
 * `--background` / `--foreground` / `--card` are deliberately absent: recolouring
 * the page body is how a colour picker turns into an unreadable screen. The
 * sidebar is the large block that makes a tab recognisable from the taskbar,
 * and primary carries the buttons.
 *
 * `--secondary` is here even though the accent barely uses it, because
 * `applyBoTheme` writes it: without an entry of its own an accent would leave
 * the brand's secondary standing beside a completely different primary, which
 * is a clash nobody chose.
 */
export const ACCENT_MANAGED_VARS = [
  '--primary',
  '--primary-foreground',
  '--ring',
  '--secondary',
  '--sidebar',
  '--sidebar-foreground',
  '--sidebar-accent',
  '--sidebar-accent-foreground',
  '--sidebar-border',
] as const;

export function accentVars(theme: AccentTheme, mode: ColorMode): Record<string, string> {
  const r = ramp(theme, mode);
  const primary = hslTriplet(r.primary);
  const onPrimary = hslTriplet(r.primaryForeground);
  return {
    '--primary': primary,
    '--primary-foreground': onPrimary,
    '--ring': primary,
    '--secondary': hslTriplet(r.secondary),
    '--sidebar': hslTriplet(r.sidebar),
    '--sidebar-foreground': hslTriplet(r.sidebarForeground),
    '--sidebar-accent': primary,
    '--sidebar-accent-foreground': onPrimary,
    '--sidebar-border': hslTriplet(r.sidebarBorder),
  };
}

/**
 * The dot the picker draws for a theme — a complete `hsl()`, ready for a style
 * prop. Comma-separated on purpose: the space-separated CSS Color 4 form is
 * what the custom properties carry (Tailwind composes them as
 * `hsl(var(--primary))`), but as a whole declaration value it is dropped by
 * stricter parsers, which leaves the swatch colourless rather than wrong.
 */
export function accentSwatch(theme: AccentTheme, mode: ColorMode): string {
  const [h, s, l] = ramp(theme, mode).primary;
  return `hsl(${h}, ${s}%, ${l}%)`;
}

/**
 * The accent paints on <body>, not <html>, and that is load-bearing.
 *
 * `applyBoTheme` writes the brand's colour as inline properties on <html>.
 * Custom properties inherit, so the same name set one level down on <body>
 * wins for every element inside it — which is everything, Radix portals
 * included, since they mount into `document.body`. Sharing <html> instead
 * would mean each applier stomping the other's keys and the two having to run
 * in a fixed order after every brand refetch; on separate elements the accent
 * simply layers over the brand and either can be cleared alone.
 */
function accentElement(): HTMLElement | null {
  return typeof document === 'undefined' ? null : document.body;
}

/** Drop every accent-owned property, leaving whatever the brand or the stylesheet says. */
export function clearAccent(): void {
  const el = accentElement();
  if (!el) return;
  for (const name of ACCENT_MANAGED_VARS) el.style.removeProperty(name);
}

/**
 * Paint the chosen accent, replacing any previous one. `DEFAULT_ACCENT` (or an
 * id no longer shipped) clears back to the brand colour.
 */
export function applyAccent(id: AccentId, mode: ColorMode): void {
  const el = accentElement();
  if (!el) return;
  clearAccent();
  const theme = accentTheme(id);
  if (!theme) return;
  for (const [name, value] of Object.entries(accentVars(theme, mode))) {
    el.style.setProperty(name, value);
  }
}
